import {
  ImportProjectState,
  ImportActions,
  ImportGetters,
  ImportMutations,
  ImportState,
  ImportErrors,
  InitialDataHash,
  ImportLanguages
} from "@medapro-types/Imports/store/imports";
import { sha1 as hash } from "object-hash";
import { ImportSection, ImportType } from "@medapro-types/Imports/import-enum";
import importApi from "@services/imports";
import configurationManagerApi from "@services/configurationManager";
import { Guid } from "@util/guid";
import { CSVConnectionModel } from "@medapro-types/common/common";
import { ImportProgressState } from "@medapro-types/Imports/components/ProgressImport";
import { ExternalSourceConnection } from "@medapro-types/Imports/Import";

const defaultState = (): ImportState => ({
  runningPromises: [],
  isSaving: false,
  saveMessageSeen: true,
  importProjects: [],
  mappings: [],
  mappingIdsToDelete: [],
  canDeleteExistingStructure: false,
  csvConnectionModel: null,
  showManageImports: false,
  initialDataHash: {
    selectedImport: hash(null),
    sourceStructure: hash([]),
    mappings: hash([])
  },
  selectedImport: null,
  sourceStructure: [],
  targetStructure: {
    canEditArticleNo: false,
    targetstructure: []
  },
  translationNodeIds: [],
  errors: {
    source: false,
    mapping: false,
    importRules: false,
    options: false,
    name: false
  },
  validationErrors: {
    source: false,
    mapping: false,
    importRules: false,
    options: false,
    name: false
  },
  type: null,
  externalSourceConnections: [],
  importId: "",
  isUploadedNewSourceFile: false,
  uploadId: null,
  rules: [],
  selectedProgressInfo: {} as ImportProgressState,
  showProgress: false,
  abortController: null,
  isStoreLoading: false
});

const getters: ImportGetters = {
  hasUnsavedData: function (state: ImportState, getters: any) {
    const keys: (keyof InitialDataHash)[] = Object.keys(state.initialDataHash) as (keyof InitialDataHash)[];
    return keys.some((x) => getters.isSectionModified(x)) || state.isUploadedNewSourceFile;
  },
  hasUploadInProgress: function (state: ImportState) {
    return state.showProgress;
  },
  isBusy: function (state: ImportState) {
    return !!state.runningPromises.length;
  },
  lastRunSuccess: function (state: ImportState) {
    const result = Object.values(state.errors).every((x) => !x);
    return result;
  },
  isSaving: function (state: ImportState) {
    return state.isSaving;
  },
  sectionHasError: (state: ImportState) => (section: string) => {
    return state.errors[section as keyof ImportErrors] ?? false;
  },
  isSectionModified: (state: ImportState) => (section: keyof InitialDataHash) => {
    const currentHash = state.initialDataHash[section];
    const newHash = hash(state[section]);
    return currentHash !== newHash;
  },
  hasValidationError: function (state: ImportState) {
    const result = Object.values(state.validationErrors).some((x) => !!x);
    return result;
  },
  sectionHasValidationError: (state: ImportState) => (section: string) => {
    return state.validationErrors[section as keyof ImportErrors] ?? false;
  },
  sourceTableNodeMap: (state: ImportState) => {
    //  Get all valid source field names from the source structure under source table nodes
    return state.sourceStructure.reduce(
      (acc: { [key: string]: string[] }, table: any) => {
        acc[table.name] = table.sourceTableNodes.map((node: any) => node.name);
        return acc;
      },
      {} as { [key: string]: string[] }
    );
  },
  importLanguages(state: ImportState): ImportLanguages | null {
    if (!state.selectedImport?.options) return null;

    try {
      const options = JSON.parse(state.selectedImport.options);

      return {
        importLanguageId: options.importLanguageId ?? null,
        translationLanguageId: options.translationLanguageId ?? null
      };
    } catch {
      return null;
    }
  }
};
const mutations: ImportMutations = {
  "reset-state"(state) {
    Object.assign(state, defaultState());
  },
  "set-type": function (state: ImportState, args: ImportType) {
    state.type = args;
  },
  "set-initial-hash": function (state: ImportState, args: { section: keyof InitialDataHash; data: any }) {
    state.initialDataHash[args.section] = hash(args.data);
  },
  "update-notification-status": function (state: ImportState, args: boolean) {
    state.saveMessageSeen = args;
  },
  "add-running-promise": function (state: ImportState, promise: Promise<any>) {
    state.runningPromises.push(promise);
  },
  "reset-running-promises": function (state: ImportState) {
    state.runningPromises = [];
  },
  "set-import-projects": function (state: ImportState, args: ImportProjectState[]) {
    state.importProjects = args;
  },
  "set-selected-import": function (state: ImportState, args: ImportProjectState) {
    state.selectedImport = args;
  },
  "set-source-structure": function (state: ImportState, args: any) {
    state.sourceStructure = args;
  },
  "set-target-structure": function (state: ImportState, args: any) {
    state.targetStructure = args;
  },
  "set-translation-node-ids": function (state: ImportState, args: any) {
    state.translationNodeIds = args;
  },
  "set-mappings": function (state: ImportState, args: any) {
    state.mappings = args;
  },
  "set-mapping-ids-to-delete": function (state: ImportState, args: any) {
    state.mappingIdsToDelete = args;
  },
  "set-error": function (state: ImportState, args: { section: string; error: boolean }) {
    const errors = Object.assign({}, state.errors);
    errors[args.section as keyof ImportErrors] = args.error;
    state.errors = errors;
  },
  "set-validation-error": function (state: ImportState, args: { section: string; error: boolean }) {
    const errors = Object.assign({}, state.validationErrors);
    errors[args.section as keyof ImportErrors] = args.error;
    state.validationErrors = errors;
  },
  "set-is-saving": function (state: ImportState, args: boolean) {
    state.isSaving = args;
  },
  "reset-errors": function (state: ImportState) {
    const keys: (keyof ImportErrors)[] = Object.keys(state.errors) as (keyof ImportErrors)[];
    keys.forEach((key) => (state.errors[key] = false));
  },
  "set-import-id": function (state: ImportState, args: string) {
    state.importId = args;
  },
  "set-is-uploaded-new-source-file": function (state: ImportState, args: boolean) {
    state.isUploadedNewSourceFile = args;
  },
  "set-source-upload-id": function (state: ImportState, args: string) {
    state.uploadId = args;
  },
  "set-csv-connection-model": function (state: ImportState, args: CSVConnectionModel) {
    state.csvConnectionModel = args;
  },
  "set-option-can-delete-existing-structure": function (state: ImportState, args: boolean) {
    state.canDeleteExistingStructure = args;
  },
  "set-show-manage-imports": function (state: ImportState, args: boolean) {
    state.showManageImports = args;
  },
  "set-rules": function (state: ImportState, args: any) {
    state.rules = args;
  },
  "set-progress-info": function (state: ImportState, args: ImportProgressState) {
    state.selectedProgressInfo = args;
  },
  "set-show-progress": function (state: ImportState, args: boolean) {
    state.showProgress = args;
  },
  "set-abort-controller": function (state: ImportState, args: AbortController | null) {
    state.abortController = args;
  },
  "set-is-store-loading": function (state: ImportState, args: boolean) {
    state.isStoreLoading = args;
  },
  "set-options": function (state: ImportState, args: any) {
    if (state.selectedImport) {
      state.selectedImport = { ...state.selectedImport, options: args };
    }
  },
  "set-external-source-connections": function (state: ImportState, args: ExternalSourceConnection[]) {
    state.externalSourceConnections = args;
  }
};
const actions: ImportActions = {
  setType: async function ({ commit, dispatch, state }, args: ImportType) {
    const currentType = state.type;
    commit("set-type", args);
    if (currentType !== args) {
      await dispatch("loadImportProjects");
    }
    if (args === ImportType.ExternalSource) {
      await dispatch("loadExternalSourceConnections");
    }
  },
  resetRunningPromisesIfResolved: async function ({ state, commit }) {
    await Promise.all(state.runningPromises);
    commit("reset-running-promises");
  },
  loadRules: async function ({ state, commit, dispatch }) {
    if (!state.rules?.length) {
      const promise = configurationManagerApi.getRules();
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-rules", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  loadImportProjects: async function ({ state, commit, dispatch }) {
    try {
      const promise = importApi.getAllImportProjects(state.type);
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-import-projects", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    } catch (error) {
      commit("set-import-projects", []);
    }
  },
  loadExternalSourceConnections: async function ({ state, commit, dispatch }) {
    if (state.externalSourceConnections.length) return; // Already loaded
    const promise = importApi.getExternalSourceConnections();
    commit("add-running-promise", promise);
    const fetchedResult = await promise;
    commit("set-external-source-connections", fetchedResult);
    await dispatch("resetRunningPromisesIfResolved");
  },
  loadImportSourceStructure: async function ({ commit, dispatch, state }, args: any) {
    if (!state.sourceStructure.length || args.force) {
      const promise = importApi.getSourceStructure(args.importId);
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-source-structure", fetchedResult);
      commit("set-initial-hash", { section: "sourceStructure", data: fetchedResult });
      await dispatch("resetRunningPromisesIfResolved");
    } else if (!args.force) {
      commit("set-initial-hash", { section: "sourceStructure", data: state.sourceStructure });
    }
  },
  loadImportTargetStructure: async function ({ state, commit, dispatch, rootState }) {
    const options = JSON.parse(state.selectedImport?.options ?? "{}");
    const importLanguageCode = rootState.metadata?.localizedLanguages.find((x) => x.languageId == options.importLanguageId)?.languageCode;
    if (importLanguageCode) {
      const promise = importApi.getTargetStructure(importLanguageCode);
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-target-structure", fetchedResult);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  collectTranslationNodeIds: function ({ state, commit }) {
    const translationNodeIds: string[] = [];
    const collectNodeIds = (nodes: any[]) => {
      nodes.forEach((node) => {
        if (node.isTranslationNode && node.name) {
          translationNodeIds.push(node.nodeId);
        }
        if (node.targetNodes?.length) {
          node.targetNodes.forEach((targetNode: any) => {
            if (targetNode.isTranslationNode && targetNode.name) {
              translationNodeIds.push(targetNode.nodeId);
            }
            if (targetNode.children?.length) {
              collectNodeIds(targetNode.children);
            }
          });
        }
        if (node.children?.length) {
          collectNodeIds(node.children);
        }
      });
    };
    if (state.targetStructure.targetstructure.length) {
      collectNodeIds(state.targetStructure.targetstructure);
    }
    commit("set-translation-node-ids", translationNodeIds);
  },
  loadImportMappings: async function ({ state, commit, dispatch }) {
    if (state.importId && !Guid.isEmpty(state.importId)) {
      const promise = importApi.getMappings(state.importId);
      commit("add-running-promise", promise);
      const fetchedResult = await promise;
      commit("set-mappings", fetchedResult);
      commit("set-initial-hash", { section: "mappings", data: fetchedResult });
      commit("set-mapping-ids-to-delete", []);
      await dispatch("resetRunningPromisesIfResolved");
    }
  },
  setSourceStructureForExternalSource: async function ({ state, commit, dispatch }, connectionId: string) {
    try {
      commit("set-error", { section: "source", error: false });
      const result = await importApi.getSourceStructureForExternalSourceImport(connectionId);
      commit("set-is-uploaded-new-source-file", true);
      commit("set-source-upload-id", connectionId);
      commit("set-selected-import", { ...state.selectedImport, source: connectionId, isSourceExists: true });
      commit("set-source-structure", result);
      if (state.canDeleteExistingStructure) {
        // Delete all existing mappings if the source structure is changed.
        commit("set-mappings", []);
      } else {
        // Keep existing valid mappings, and delete the invalid ones, if any.
        await dispatch("deleteAndKeepValidMappingsIfAny");
      }
      await dispatch("setValidationErrorsIfAny");
    } catch (error: any) {
      commit("set-error", { section: "source", error: true });
    }
  },
  setImportId: function ({ commit }, args: string) {
    commit("set-import-id", args);
  },
  setSelectedImport: function ({ commit, state }, args: ImportProjectState) {
    if (!Guid.isEmpty(args.importId) && state.selectedImport?.importId != args.importId) {
      commit("set-initial-hash", { section: "selectedImport", data: args });
    }
    commit("set-selected-import", args);
  },
  initializeSelectedImport: async function ({ commit, state, dispatch }, args: ImportProjectState) {
    commit("set-is-store-loading", hash(args) !== hash(state.selectedImport) || false);
    if (!Guid.isEmpty(args.importId) && state.selectedImport?.importId != args.importId) {
      commit("set-selected-import", args);
      commit("set-initial-hash", { section: "selectedImport", data: args });
      commit("set-import-id", args.importId);
      await dispatch("loadImportSourceStructure", { importId: args.importId, force: true });
      await dispatch("loadImportMappings");
    } else if (Guid.isEmpty(args.importId)) {
      commit("set-selected-import", args);
      commit("set-source-structure", []);
      commit("set-initial-hash", { section: "sourceStructure", data: [] });
      commit("set-mappings", []);
      commit("set-initial-hash", { section: "mappings", data: [] });
    }
    commit("set-is-uploaded-new-source-file", false);
    await dispatch("setValidationErrorsIfAny");
    commit("set-is-store-loading", false);
  },
  setValidationErrorsIfAny: function ({ commit, state }) {
    if (state.selectedImport?.source === "" || !state.selectedImport?.isSourceExists) {
      commit("set-validation-error", { section: "source", error: true });
    } else {
      commit("set-validation-error", { section: "source", error: false });
    }
    if (state.mappings.length === 0) {
      commit("set-validation-error", { section: "mapping", error: true });
    } else {
      commit("set-validation-error", { section: "mapping", error: false });
    }
    if (state.errors.importRules) {
      commit("set-validation-error", { section: "importRules", error: true });
    } else {
      commit("set-validation-error", { section: "importRules", error: false });
    }

    if (state.selectedImport?.options) {
      const options = JSON.parse(state.selectedImport.options);
      if (options.articleNoFilters && options.articleNoFilters.length > 0) {
        const hasValidationError = options.articleNoFilters.some((filter: any) => {
          return !filter.name || !filter.expression;
        });
        commit("set-validation-error", { section: ImportSection.ArticleNoFilter, error: hasValidationError });
      }
    }
  },
  saveImportSetings: async function ({ state, commit, dispatch, getters }) {
    try {
      if (getters.hasUnsavedData) {
        commit("set-is-saving", true);
        commit("reset-errors");
        commit("update-notification-status", false);
        if (getters.isSectionModified("selectedImport")) {
          await dispatch("saveImport");
        }
        if (getters.isSectionModified("sourceStructure") || state.isUploadedNewSourceFile) {
          await dispatch("saveSourceStructure");
        }
        if (getters.isSectionModified("mappings")) {
          if (state.mappingIdsToDelete.length) {
            await importApi.deleteMappings(state.mappingIdsToDelete);
            state.mappingIdsToDelete = [];
          }
          if (state.mappings.length) {
            await dispatch("saveMappings");
          }
          commit("set-initial-hash", { section: "mappings", data: state.mappings });
        }
        if (state.mappings.length) {
          await dispatch("loadImportMappings");
        }
      }
    } catch (error) {
      console.log("Error saving import settings: ", error);
    } finally {
      commit("set-is-uploaded-new-source-file", false);
      commit("set-is-saving", false);
      dispatch("setValidationErrorsIfAny");
    }
  },
  saveImport: async function ({ state, commit, getters }) {
    try {
      if (getters.hasUnsavedData && state.selectedImport) {
        const importProjectId = await importApi.save(state.selectedImport);
        let updatedProjects = [];
        if (state.selectedImport.importId === Guid.empty) {
          const newImport = { ...state.selectedImport, importId: importProjectId };
          updatedProjects = [...state.importProjects, newImport];
          commit("set-import-id", importProjectId);
          commit("set-selected-import", newImport);
          commit("set-import-projects", updatedProjects);
        } else {
          updatedProjects = state.importProjects.map((project) =>
            state.selectedImport && project.importId === importProjectId ? state.selectedImport : project
          );
          commit("set-import-projects", updatedProjects);
        }
        commit("set-initial-hash", { section: "selectedImport", data: state.selectedImport });
      }
    } catch (error: any) {
      if (error.isAxiosError && error.response?.data?.error === "NameExist") {
        commit("set-error", { section: "name", error: true });
      } else {
        commit("set-error", { section: "options", error: true });
      }
      throw new Error("During saving an Error occured");
    }
  },
  saveSourceStructure: async function ({ state, commit, dispatch }) {
    try {
      if (state.selectedImport && state.selectedImport.importId != Guid.empty && state.sourceStructure) {
        const result = await importApi.saveSourceStructure(
          {
            importId: state.importId,
            fileName: state.selectedImport.source,
            uploadId: state.uploadId,
            canDeleteExistingStructure: state.canDeleteExistingStructure
          },
          state.csvConnectionModel
        );
        if (state.type !== ImportType.ExternalSource) commit("set-selected-import", { ...state.selectedImport, source: result.fileName });
        commit("set-initial-hash", { section: "selectedImport", data: state.selectedImport });
        await dispatch("loadImportSourceStructure", { importId: state.importId, force: false });
      }
    } catch (error) {
      commit("set-error", { section: "source", error: true });
      throw new Error("During saving an Error occured");
    }
  },
  saveMappings: async function ({ state, commit }) {
    try {
      if (state.selectedImport && state.selectedImport.importId != Guid.empty && state.mappings) {
        await importApi.saveMappings(state.importId, state.mappings);
        commit("set-mappings", state.mappings);
        commit("set-initial-hash", { section: "mappings", data: state.mappings });
      }
    } catch (error) {
      commit("set-error", { section: "mapping", error: true });
      throw new Error("During saving an Error occured");
    }
  },
  copyImport: async function ({ state, commit, rootState }) {
    try {
      if (state.selectedImport && state.selectedImport.importId != Guid.empty) {
        const response = await importApi.copyProject(state.importId, rootState.userSettings.CurrentLocale);
        commit("set-import-projects", [...state.importProjects, response]);
        return response;
      }
    } catch (error) {
      throw new Error("During copying an Error occured");
    }
  },
  deleteImport: async function ({ state, commit, dispatch }, importId: string[]) {
    try {
      if (importId && importId.length !== 0) {
        await importApi.delete(importId);
        const updatedProjects = state.importProjects.filter((project) => !importId.includes(project.importId));
        commit("set-import-projects", updatedProjects);
        if (state.selectedImport && importId.includes(state.selectedImport.importId)) {
          const firstImport = state.importProjects && state.importProjects[0];
          if (firstImport) {
            await dispatch("initializeSelectedImport", firstImport);
          }
        }
      }
    } catch (error) {
      console.error("Error deleting import projects: ", error);
    }
  },
  setError: function ({ commit }, args?: any) {
    commit("set-error", args);
  },
  setImportName: function ({ commit, state }, args: string) {
    commit("set-selected-import", { ...state.selectedImport, name: args });
    commit("set-error", { section: "name", error: false });
  },
  uploadSourceFile: async function ({ commit, state, dispatch }, args: { file: File }) {
    try {
      commit("set-error", { section: "source", error: false });
      const result = await importApi.uploadSourceFile(
        state.selectedImport?.importId ?? Guid.empty,
        state.type,
        state.csvConnectionModel,
        args.file,
        state.abortController?.signal
      );
      commit("set-source-upload-id", result.uploadId);
      commit("set-selected-import", { ...state.selectedImport, source: args.file.name, isSourceExists: true });
      commit("set-source-structure", result.sourceTables);
      if (state.canDeleteExistingStructure) {
        // Delete all existing mappings if the source structure is changed.
        commit("set-mappings", []);
      } else {
        // Keep existing valid mappings, and delete the invalid ones, if any.
        await dispatch("deleteAndKeepValidMappingsIfAny");
      }
      await dispatch("setValidationErrorsIfAny");
      return state.type === ImportType.BMEcat && result.validationErrorMessage ? result.validationErrorMessage : undefined;
    } catch (error: any) {
      if (error.name !== "CanceledError") {
        commit("set-error", { section: "source", error: true });
      }
      throw error;
    }
  },
  deleteSourceFile: async function ({ commit, state, getters }) {
    try {
      commit("update-notification-status", true);
      await importApi.deleteSourceFile(state.importId);
      const updatedImportProject = { ...state.selectedImport, source: "", isSourceExists: false };
      if (!getters.isSectionModified("selectedImport")) {
        commit("set-initial-hash", { section: "selectedImport", data: updatedImportProject });
      }
      commit("set-selected-import", updatedImportProject);
      commit(
        "set-import-projects",
        state.importProjects.map((project) => (project.importId === state.selectedImport?.importId ? updatedImportProject : project))
      );
    } catch (error) {
      console.error("Error deleting import source file: ", error);
      throw error;
    }
  },
  reloadSourceFile: async function ({ dispatch, state }) {
    try {
      await importApi.reloadSourceFile(state.importId, state.abortController?.signal);
      await dispatch("loadImportSourceStructure", { importId: state.importId, force: true });
      // Load Mappings once the source structure is loaded, to make sure the mapping validation is in sync with the latest source structure.
      await dispatch("loadImportMappings");
    } catch (error) {
      console.error("Error reloading import source file: ", error);
      throw error;
    }
  },
  setProgressInfo: async function ({ commit }, args: any) {
    commit("set-progress-info", args);
  },
  deleteAndKeepValidMappingsIfAny: async function ({ state, commit, getters }) {
    if (state.selectedImport && state.selectedImport.importId != Guid.empty && state.mappings) {
      // Filter out mappings that have no sourceFields or if any sourceField is not valid
      const validMappings = state.mappings.filter((mapping) => {
        return (
          mapping.sourceFields &&
          mapping.sourceFields.every((sf) => {
            return (
              getters.sourceTableNodeMap[mapping.importSourceTableName] &&
              getters.sourceTableNodeMap[mapping.importSourceTableName].includes(sf.importSourceTableNodeName)
            );
          })
        );
      });
      commit("set-mappings", validMappings);
    }
  },
  cancelActionsIfAny: async function ({ state }) {
    if (state.abortController) {
      state.abortController.abort();
    }
  }
};

const importStoreModule = {
  namespaced: true,
  state: { ...defaultState() },
  getters,
  mutations,
  actions
};
export default importStoreModule;
